// Gibraltar uses an app-generated S256 challenge and state; iOS uses its verified HTTPS callback.
// Login, forgot-password and Microsoft sign-in preserve the complete return query for both.
//
// This module has two halves:
// - The printer sign-in redirect, for either client: the login, Forgot password and a Microsoft
//   sign-in keep the way back to the printer sign-in page.
// - The iOS app's sign-in: inside its sheet the web app must not offer account creation or
//   marketing links (App Store Review Guideline 5.1.1(v): an app that lets people create an account
//   must also let them delete it in the app) nor a way into the rest of the web app, which cannot
//   finish the sign-in, and no third party may see the page. Gibraltar's login is the ordinary login.
// AppSignInPages (app/lib/app_sign_in_pages.rb) is the server's copy of both halves; the two decide
// alike (spec/fixtures/app_sign_in_pages_conformance.json).
import { parse } from 'qs';

const PRINTER_SIGN_IN_REDIRECT = /^\/app\/printer_sign_in(?:[/?#]|$)/;
const PRINTER_SIGN_IN_PAGE = /^\/app\/printer_sign_in\/?$/;

export const IOS_APP_CLIENT = 'ios';

// Where "Log in with Microsoft" keeps the login's ?redirect= while the browser is away at
// Microsoft (NewLogin's saveCurrentUrl). LoginMicrosoft returns there after a successful sign-in.
export const SSO_RETURN_PATH_KEY = 'storedUrl';

// The printer sign-in redirect, for either client.

/**
 * Whether a pathname is the printer sign-in page itself (Gibraltar or the Medsender iOS app).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isPrinterSignInPage = (pathname) => PRINTER_SIGN_IN_PAGE.test(pathname || '');

/**
 * Whether a login's ?redirect= (or a stored or OmniAuth return path) names the printer sign-in
 * page, with any query.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const isPrinterSignInRedirect = (value) => (
  typeof value === 'string' && PRINTER_SIGN_IN_REDIRECT.test(value)
);

/**
 * The printer sign-in page a login (or a page reached from it, like Forgot password, which keeps
 * the query) is on its way to, or null. A signed-out visit to /app/printer_sign_in lands on
 * /login?redirect=<that path and query> (redux-auth-wrapper).
 *
 * @param {string} search the page's query string
 * @returns {string|null} the redirect, like /app/printer_sign_in?client=ios
 */
export const printerSignInRedirect = (search) => {
  const { redirect } = parse(search || '', { ignoreQueryPrefix: true });
  return isPrinterSignInRedirect(redirect) ? redirect : null;
};

/**
 * The query string that keeps the way back to the printer sign-in page: the page's own search when
 * it is on its way there, otherwise ''. Forgot password's Return to Log in carries it.
 *
 * @param {string} search the current page's query string
 * @returns {string}
 */
export const printerSignInSearch = (search) => (printerSignInRedirect(search) !== null ? search : '');

/**
 * The printer sign-in page a Microsoft sign-in in this tab started from, or null.
 * sessionStorage holds the string 'null' when the login had no redirect.
 *
 * @returns {string|null} the stored path and query, like /app/printer_sign_in?client=ios
 */
export const storedPrinterSignInRedirect = () => {
  try {
    const stored = sessionStorage.getItem(SSO_RETURN_PATH_KEY);
    return isPrinterSignInRedirect(stored) ? stored : null;
  } catch {
    return null;
  }
};

/**
 * On a login back from a failed Microsoft sign-in (`?failedSSO=true`) or SAML sign-in
 * (`?ssoError=<code>`), the printer sign-in page that sign-in started from, or null. A failure URL
 * without the login's ?redirect= (OmniAuth's origin lost, an older link, every SAML failure)
 * relies on this; the login puts it back as its ?redirect=.
 *
 * @param {string} search the login page's query string
 * @returns {string|null}
 */
export const failedSsoPrinterSignInRedirect = (search) => {
  const { failedSSO, ssoError } = parse(search || '', { ignoreQueryPrefix: true });
  return failedSSO || ssoError ? storedPrinterSignInRedirect() : null;
};

/**
 * The printer sign-in page the login is on its way to: its own ?redirect=, or else the one a failed
 * Microsoft sign-in started from. null for an ordinary login.
 *
 * @param {string} search the login page's query string
 * @returns {string|null}
 */
export const loginPrinterSignInRedirect = (search) => (
  printerSignInRedirect(search) ?? failedSsoPrinterSignInRedirect(search)
);

// The iOS app's sign-in.

/**
 * Whether a query string has `client=ios` (the Medsender iOS app), parsed by qs: a repeated or
 * bracketed client parameter is not the string 'ios'.
 *
 * @param {string} search a query string, with or without its leading '?'
 * @returns {boolean}
 */
export const isIosAppClient = (search) => (
  parse(search || '', { ignoreQueryPrefix: true }).client === IOS_APP_CLIENT
);

/**
 * Whether a redirect (or stored return path) names the iOS app's printer sign-in page: the page
 * itself with client=ios in its own query (before any fragment).
 *
 * @param {*} value
 * @returns {boolean}
 */
export const isIosAppSignInRedirect = (value) => {
  if (!isPrinterSignInRedirect(value)) return false;
  const [pathAndQuery] = value.split('#');
  const queryStart = pathAndQuery.indexOf('?');
  return queryStart !== -1
    && isPrinterSignInPage(pathAndQuery.slice(0, queryStart))
    && isIosAppClient(pathAndQuery.slice(queryStart));
};

/**
 * Whether this is the printer sign-in page opened by the Medsender iOS app.
 *
 * @param {{ pathname: string, search: string }} location
 * @returns {boolean}
 */
export const isIosAppSignInPage = ({ pathname, search } = {}) => (
  isPrinterSignInPage(pathname) && isIosAppClient(search)
);

/**
 * Whether a login (or Forgot password, which keeps its query) belongs to the iOS app's sign-in:
 * on its way to the iOS app's printer sign-in page, or back from a failed Microsoft sign-in that
 * started there.
 *
 * @param {string} search the page's query string
 * @returns {boolean}
 */
export const isIosAppSignInLoginPage = (search) => (
  isIosAppSignInRedirect(loginPrinterSignInRedirect(search))
);

const LOGIN_PATH = /^\/login\/?$/;
const FORGOT_PASSWORD_PATH = /^\/app\/forgot_password\/?$/;
const LOGIN_MICROSOFT_PATH = /^\/app\/login_microsoft\/?$/;

/**
 * Whether a page belongs to the iOS app's sign-in, where no third-party script (Intercom,
 * GrowSurf, ProfitWell, PostHog) may load or boot, no account data may leave to third parties and
 * browser Sentry sends nothing:
 * - the printer sign-in page opened by the iOS app (`?client=ios`);
 * - the login on its way to it, also back from a failed Microsoft sign-in that started there;
 * - Forgot password reached from that login (it keeps the ?redirect=);
 * - the return from a Microsoft sign-in that started there (/app/login_microsoft, which
 *   AuthController#callback gives the ?redirect=; the tab's stored path covers older links).
 * Gibraltar's printer sign-in page (no client), its login and every other page are ordinary.
 * AppSignInPages.ios_page? (app/lib/app_sign_in_pages.rb) makes the same decision for the
 * server-rendered load, which has no sessionStorage.
 *
 * @param {{ pathname: string, search: string }} location
 * @returns {boolean}
 */
export const isIosAppSignInFlowPage = ({ pathname, search } = {}) => {
  const path = pathname || '';
  if (isPrinterSignInPage(path)) return isIosAppClient(search);
  if (LOGIN_PATH.test(path)) return isIosAppSignInLoginPage(search);
  if (FORGOT_PASSWORD_PATH.test(path)) return isIosAppSignInRedirect(printerSignInRedirect(search));
  if (LOGIN_MICROSOFT_PATH.test(path)) {
    return isIosAppSignInRedirect(printerSignInRedirect(search))
      || isIosAppSignInRedirect(storedPrinterSignInRedirect());
  }
  return false;
};

// Old desktop versions open the plain page. Preserve their sign-in during migration, but
// never interpret an incomplete/ambiguous proof request as permission to use the legacy flow.
export const gibraltarSignInRequest = (search) => {
  const params = parse(search || '', { ignoreQueryPrefix: true });
  if (!['client', 'code_challenge', 'code_challenge_method', 'state'].some((key) => Object.prototype.hasOwnProperty.call(params, key))) return {};
  const {
    client, code_challenge: codeChallenge, code_challenge_method: codeChallengeMethod, state,
  } = params;
  const valid = (value) => typeof value === 'string' && /^[A-Za-z0-9_-]{43}$/.test(value);
  if (client !== 'gibraltar' || codeChallengeMethod !== 'S256' || !valid(codeChallenge) || !valid(state)) return null;
  return {
    client, code_challenge: codeChallenge, code_challenge_method: codeChallengeMethod, state,
  };
};
